import type { InputRef } from "../../../../../../shared/frontend/ui";
import { KEY_ENTER, KEY_ESCAPE } from "../../../../../../shared/frontend/ui/keyboardKeys";
import { errorMessage } from "./errorMessage";
import { App,Button,Input,Space,Typography } from "../../../../../../shared/frontend/ui";
import React from "react";
import type { ApiClient } from "../api/client";
import { spacing } from "../design-system";
import { trStatic } from "../i18n/static";

const FOCUS_DELAY_MS = 100;
const ABBREVIATION_MAX_LENGTH = 20;

export type AccountDto = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  name: string;
  abbreviation?: string | null;
  isActive: boolean;
};

// Interne Komponente für das Kürzel-Modal.
function AbbreviationModalContent(props: {
  actionDescription: React.ReactNode;
  onSubmit: (abbreviation: string) => void;
  onCancel: () => void;
  loading: boolean;
  error: string | null;
}) {
  const inputRef = React.useRef<InputRef>(null);
  const [abbreviation, setAbbreviation] = React.useState("");

  React.useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, FOCUS_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = (e.target.value || "").trim().slice(0, ABBREVIATION_MAX_LENGTH);
    setAbbreviation(val);
  };

  const handleSubmit = () => {
    const a = abbreviation.trim();
    if (a.length > 0) props.onSubmit(a);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === KEY_ESCAPE) {
      props.onCancel();
    }
    if (e.key === KEY_ENTER) {
      handleSubmit();
    }
  };

  return (
    <div>
      <Typography.Text type="secondary">{props.actionDescription}</Typography.Text>
      <div style={{ marginTop: spacing.md }}>
        <Typography.Text>{trStatic("Abbreviation")}</Typography.Text>
        <Space.Compact style={{ width: "100%", marginTop: spacing.controlCompact }}>
          <Input
            ref={inputRef}
            placeholder={trStatic("Your abbreviation for confirmation (e.g. MK)")}
            value={abbreviation}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={props.loading}
            maxLength={20}
            style={{ textTransform: "uppercase" }}
          />
          <Button type="primary" loading={props.loading} onClick={handleSubmit} disabled={!abbreviation.trim()}>
            {trStatic("Confirm")}
          </Button>
        </Space.Compact>
        {props.error ? (
          <div style={{ marginTop: spacing.controlCompact }}>
            <Typography.Text type="danger">{props.error}</Typography.Text>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export async function requireAccountAbbreviationConfirmation(opts: {
  api: ApiClient;
  modal: ReturnType<typeof App.useApp>["modal"];
  actionDescription: React.ReactNode;
}): Promise<AccountDto> {
  const currentUser = await opts.api.me();
  if (!currentUser.isCollectionUser) {
    return {
      id: currentUser.accountId,
      name: currentUser.displayName,
      isActive: true
    };
  }

  return await new Promise<AccountDto>((resolve, reject) => {
    let loading = false;
    let error: string | null = null;
    let modalInstance: ReturnType<typeof opts.modal.confirm> | null = null;

    const doSubmit = async (abbreviation: string) => {
      if (loading) return;
      const a = (abbreviation || "").trim();
      if (!a) {
        error = trStatic("Please enter an abbreviation.");
        updateModal();
        return;
      }

      loading = true;
      error = null;
      updateModal();

      try {
        const account = await opts.api.post<AccountDto>("/api/v1/users/verify-abbreviation", { abbreviation: a });
        modalInstance?.destroy();
        resolve(account);
      } catch (e: unknown) {
        loading = false;
        error = errorMessage(e, trStatic("Abbreviation is invalid or not found"));
        updateModal();
      }
    };

    const doCancel = () => {
      modalInstance?.destroy();
      reject(new Error(trStatic("Cancelled")));
    };

    const updateModal = () => {
      modalInstance?.update({
        content: (
          <AbbreviationModalContent
            actionDescription={opts.actionDescription}
            onSubmit={doSubmit}
            onCancel={doCancel}
            loading={loading}
            error={error}
          />
        ),
        okButtonProps: { style: { display: "none" } },
        cancelButtonProps: { disabled: loading }
      });
    };

    modalInstance = opts.modal.confirm({
      title: trStatic("Abbreviation for confirmation"),
      icon: null,
      okText: trStatic("Confirm"),
      cancelText: trStatic("Cancel"),
      okButtonProps: { style: { display: "none" } },
      content: (
        <AbbreviationModalContent
          actionDescription={opts.actionDescription}
          onSubmit={doSubmit}
          onCancel={doCancel}
          loading={loading}
          error={error}
        />
      ),
      onCancel: doCancel
    });
  });
}
